import { Component, h, Prop, Element } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';
import { getLinkTargetAttrs } from '../component-utils/ctaUtils';

/**
 * COMPNAME is for creating a container for displaying a card with an image, title, and text. The entire card functions as a link to the content.
 */
@Component({
  tag: 'aafp-journal-article-preview-card',
  shadow: false,
})
export class AafpJournalArticlePreviewCard {
  /**
   * For the title of the card
   */
  @Prop() cardTitle: string;

  /**
   * For the text of the card
   */
  @Prop() cardText: string;

  /**
   * For the src of the image
   */
  @Prop() imgSrc: string;

  /**
   * For the alt text of the image
   */
  @Prop() imgAlt: string;

  /**
   * For making the entire card clickable as a link
   */
  @Prop() linkTarget: string;

  /**
   * Should the link open in a new tab?
   */
  @Prop() linkIsExternal: boolean = false;

  @Element() element: HTMLElement;

  maybeLink(children: any, isLink: boolean = true) {
    if (!this.linkTarget || !isLink) {
      return children;
    } else {
      return (
        <a
          onClick={(e) => e.stopPropagation()}
          class="adl-journal-article-preview-card__link"
          href={this.linkTarget}
          {...getLinkTargetAttrs(this.linkIsExternal)}
        >
          {children}
        </a>
      );
    }
  }

  renderCardTitleAndText() {
    let titleAndText = [];

    if (this.cardTitle) {
      titleAndText.push(<span class="adl-journal-article-preview-card__title">{this.cardTitle}</span>);
    }

    if (this.cardText) {
      titleAndText.push(this.cardText);
    }

    return titleAndText;
  }

  onClick() {
    // abort if the user is in the middle of trying to select text
    if ((window as any).getSelection().toString().length > 0) {
      return;
    }

    const linkElement: HTMLLinkElement | null = this.element.querySelector('.adl-journal-article-preview-card__link');

    if (linkElement) {
      linkElement.click();
    }
  }

  render() {
    // Based on a pattern for accessible cards: https://inclusive-components.design/cards/
    // the entire card is clickable, but screen readers will not read out ALL
    // text content on the card as the text of the link (e.g. image alt text)
    //
    // the a tag just wraps the text (or the image if there is no text)
    return (
      <article
        class={getClassListString(
          { 'adl-journal-article-preview-card--has-link': !!this.linkTarget },
          'adl-journal-article-preview-card',
        )}
        onClick={() => this.onClick()}
      >
        {this.imgSrc &&
          this.maybeLink(
            <img class="adl-journal-article-preview-card__img" src={this.imgSrc} alt={this.imgAlt} />,
            !this.cardTitle && !this.cardText,
          )}
        {(this.cardTitle || this.cardText) && (
          <p class="adl-journal-article-preview-card__text adl-space__sm__m-v-sm">
            {this.maybeLink(this.renderCardTitleAndText())}
          </p>
        )}
      </article>
    );
  }
}
